/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useEffect, useRef } from 'react';
import { getConsolidationStateResource } from '@jira/router-resources__eoc-tenant-info/src/getConsolidationStateResource.tsx';
import type { TenantInfo } from '@jira/router-resources__eoc-tenant-info/src/TenantInfo.types.tsx';
import { useApplicationPermissions } from '@atlassian/jira-tenant-context-controller/src/components/application-permissions/index.tsx';
import { useResource, type UseResourceHookResponse } from '@atlassian/react-resource-router';
import {
	checkIsConsolidated,
	checkIsTransitionEligible,
	checkIsTransitioned,
	checkIsTransitioning,
	checkShouldAccessUnlockOperations,
	checkShouldUseJSM,
	checkIsOpsAdmin,
} from './utils.tsx';

/**
 * This hook is used to refresh the tenant info resource when the server side
 * rendering fails due to an error or never actually happens.
 */
const useConsolidationStateResourceRefresh = (
	tenantInfoResource: UseResourceHookResponse<TenantInfo>,
) => {
	const { data, loading, refresh } = tenantInfoResource;
	const isRefreshed = useRef(false);

	useEffect(() => {
		if (data === null && loading === false && isRefreshed.current === false) {
			refresh();
			isRefreshed.current = true;
		}
	}, [data, loading, refresh]);
};

export const useOgTenantInfo = (): {
	loading: boolean;
	error: // oxlint-disable-next-line @typescript-eslint/no-explicit-any
		| Record<string, any>
		| Error
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		| (Record<string, any> & Error)
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		| (Error & Record<string, any>)
		| null;
	shouldUseJSM: boolean;
	isConsolidated: boolean;
	isOpsAdmin: boolean;
	isTransitioning: boolean;
	isTransitioned: boolean;
	isTransitionEligible: boolean;
	shouldAccessUnlockOperations: boolean;
	cohort: ('cohort1' | 'cohort2' | 'cohort3' | 'cohort3.5' | 'cohort4') | null | undefined;
} => {
	const tenantInfoResource = useResource(getConsolidationStateResource());
	const applicationPermissions = useApplicationPermissions();
	useConsolidationStateResourceRefresh(tenantInfoResource);

	return {
		loading: tenantInfoResource.loading,
		error: tenantInfoResource.error,
		shouldUseJSM: checkShouldUseJSM(tenantInfoResource.data),
		isConsolidated: checkIsConsolidated(tenantInfoResource.data),
		isOpsAdmin: checkIsOpsAdmin(tenantInfoResource.data),
		isTransitioning: checkIsTransitioning(tenantInfoResource.data),
		isTransitioned: checkIsTransitioned(tenantInfoResource.data),
		isTransitionEligible:
			applicationPermissions.hasServiceDeskAccess &&
			checkIsTransitionEligible(tenantInfoResource.data),
		shouldAccessUnlockOperations:
			applicationPermissions.hasServiceDeskAccess &&
			checkShouldAccessUnlockOperations(tenantInfoResource.data),
		cohort:
			typeof tenantInfoResource.data === 'string' ? undefined : tenantInfoResource.data?.cohortInfo,
	};
};
